import {
    ApiOutlined,
    BankOutlined,
    ContactsOutlined,
    FileTextOutlined,
    GlobalOutlined,
    LogoutOutlined,
    MenuOutlined,
    NotificationOutlined,
    PieChartOutlined,
    SearchOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
    WhatsAppOutlined,
} from '@ant-design/icons'
import { Link, useMatchRoute, useNavigate, useRouter } from '@tanstack/react-router'
import {
    Alert,
    Badge,
    Button,
    Card,
    Drawer,
    Flex,
    FloatButton,
    Layout,
    Menu,
    Select,
    theme,
    Tooltip,
    Typography,
} from 'antd'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import logoLarge from '../../assets/logo-large.png'
import { useAuth } from '../../auth'
import { Container } from './container'
import { Search } from '../search'
import {
    INDEX_CAMPAIGN,
    INDEX_CANDIDATE,
    INDEX_CLIENT,
    INDEX_COMPANY_USER,
    INDEX_WHATSAPP_BUSINESS_ACCOUNT,
    UPDATE_COMPANY,
    INDEX_SITE,
} from '../../user/permission'
import { NotVerifiedAlert } from '../../user/components/not-verified-alert'
import { PaymentFailingAlert } from '../../subscription/components/payment-failing-alert'
import { LocaleSwitch } from '../../user/components/locale-switch'
import { useUpdateActiveCompany } from '../../user/hooks'
import { useLocalStorage } from '../../hooks/use-local-storage'

const { Content } = Layout

export type MainLayoutProps = { children: ReactNode }

export function MainLayout({ children }: MainLayoutProps) {
    const { t } = useTranslation()
    const [searchOpen, setSearchOpen] = useState(false)
    const matchRoute = useMatchRoute()
    const navigate = useNavigate()
    const router = useRouter()

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const { user, logout, can, stopImpersonation, isImpersonating, activeCompany, hasSubscription, websitePlan } =
        useAuth()
    const paymentFailing = !!activeCompany?.paymentFailing
    const updateActiveCompanyMutation = useUpdateActiveCompany()
    const { token } = theme.useToken()
    const [candidatesFullscreen] = useLocalStorage({
        key: 'candidates-fullscreen',
        defaultValue: false,
    })

    const menuItems = (
        user?.role === 'admin'
            ? [
                  !!user?.invitations?.length && {
                      key: '/invitations',
                      icon: <NotificationOutlined />,
                      label: (
                          <Flex
                              align="center"
                              gap="small"
                          >
                              {t('Invitations')}
                              <Badge count={user?.invitations?.length} />
                          </Flex>
                      ),
                  },
                  {
                      key: '/dashboard',
                      icon: <PieChartOutlined />,
                      label: t('Dashboard'),
                  },
                  {
                      key: '/campaigns',
                      icon: <WhatsAppOutlined />,
                      label: t('Campaigns'),
                  },

                  can(INDEX_CANDIDATE) && {
                      key: '/candidates',
                      icon: <ContactsOutlined />,
                      label: t('Candidates'),
                  },
                  can(INDEX_SITE) && {
                      key: '/websites',
                      icon: <GlobalOutlined />,
                      label: t('Websites'),
                  },
                  {
                      key: '/users',
                      icon: <UserOutlined />,
                      label: t('Users'),
                  },
                  {
                      key: '/companies',
                      icon: <BankOutlined />,
                      label: t('Companies'),
                  },
                  can(INDEX_WHATSAPP_BUSINESS_ACCOUNT) && {
                      key: '/whatsapp-business-accounts',
                      icon: <FileTextOutlined />,
                      label: t('WhatsApp business accounts'),
                  },
                  {
                      key: '/colleagues',
                      icon: <TeamOutlined />,
                      label: t('Colleagues'),
                  },
                  can(INDEX_CLIENT) &&
                      hasSubscription && {
                          key: '/clients',
                          icon: <ContactsOutlined />,
                          label: t('Clients'),
                      },
                  {
                      key: '/integrations',
                      icon: <ApiOutlined />,
                      label: t('Integrations'),
                  },
                  can(UPDATE_COMPANY) && {
                      key: '/settings',
                      icon: <SettingOutlined />,
                      label: t('Settings'),
                  },
              ]
            : [
                  !!user?.invitations?.length && {
                      key: '/invitations',
                      icon: <NotificationOutlined />,
                      label: (
                          <Flex
                              align="center"
                              gap="small"
                          >
                              {t('Invitations')}
                              <Badge count={user?.invitations?.length} />
                          </Flex>
                      ),
                  },
                  // The Website plan's home is the websites.
                  !websitePlan && {
                      key: '/dashboard',
                      icon: <PieChartOutlined />,
                      label: t('Dashboard'),
                  },
                  can(INDEX_CAMPAIGN) && {
                      key: '/campaigns',
                      icon: <WhatsAppOutlined />,
                      label: t('Campaigns'),
                  },
                  can(INDEX_CANDIDATE) &&
                      hasSubscription && {
                          key: candidatesFullscreen ? '/board' : '/candidates',
                          icon: <ContactsOutlined />,
                          label: t('Candidates'),
                      },
                  // Like campaigns, websites can be set up before subscribing.
                  can(INDEX_SITE) && {
                      key: '/websites',
                      icon: <GlobalOutlined />,
                      label: t('Websites'),
                  },
                  can(INDEX_COMPANY_USER) &&
                      hasSubscription &&
                      hasSubscription && {
                          key: '/colleagues',
                          icon: <TeamOutlined />,
                          label: t('Colleagues'),
                      },
                  can(INDEX_CLIENT) &&
                      hasSubscription &&
                      hasSubscription && {
                          key: '/clients',
                          icon: <ContactsOutlined />,
                          label: t('Clients'),
                      },
                  can(UPDATE_COMPANY) &&
                      hasSubscription && {
                          key: '/integrations',
                          icon: <ApiOutlined />,
                          label: t('Integrations'),
                      },
                  can(UPDATE_COMPANY) && {
                      key: '/settings',
                      icon: <SettingOutlined />,
                      label: t('Settings'),
                  },
              ]
    ).filter((items) => items !== false)

    const getSelectedKeys = () => {
        const matchedMenuItem = menuItems.find(({ key }) => {
            return matchRoute({ to: key, fuzzy: true }) !== false
        })

        return [matchedMenuItem?.key || '']
    }

    const selectedKeys = getSelectedKeys()

    useEffect(() => {
        document.addEventListener('keydown', (event) => {
            if (event.target instanceof HTMLInputElement) {
                return
            }

            if (event.target instanceof HTMLTextAreaElement) {
                return
            }

            if (event.key === '/') {
                event.preventDefault()
                event.stopPropagation()
                setSearchOpen(true)
            }

            return
        })
    })

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Content>
                <div className="xl:hidden">
                    <Drawer
                        closable={false}
                        placement="left"
                        open={mobileMenuOpen}
                        onClose={() => setMobileMenuOpen(false)}
                        styles={{ body: { padding: 0 } }}
                        title={
                            <img
                                alt="logo"
                                width="169"
                                src={logoLarge}
                            />
                        }
                    >
                        <Menu
                            selectedKeys={selectedKeys}
                            onClick={({ key }) => {
                                setMobileMenuOpen(false)
                                return navigate({ to: key })
                            }}
                            items={menuItems}
                            mode="inline"
                        />
                    </Drawer>
                    <FloatButton
                        icon={<MenuOutlined />}
                        onClick={() => setMobileMenuOpen(true)}
                    />
                </div>
                <Container>
                    <Flex
                        justify="space-between"
                        align="center"
                        className="mb-6 mt-6 gap-4 border-b border-hairline pb-4 flex-col xl:flex-row xl:gap-0"
                    >
                        <Flex
                            gap="middle"
                            align="center"
                            className="flex-col min-w-0 xl:flex-row xl:flex-1"
                        >
                            <Link
                                to="/dashboard"
                                className="shrink-0"
                            >
                                <img
                                    alt="logo"
                                    width="150"
                                    src={logoLarge}
                                />
                            </Link>

                            <Typography.Title
                                className="font-medium mb-0 shrink-0 whitespace-nowrap"
                                level={4}
                            >
                                {t('Hello, {{name}}', { name: user?.name })}
                            </Typography.Title>

                            <div
                                id="breadcrumb-portal"
                                className="ml-5 min-w-0 flex-1 hidden xl:block"
                            />
                        </Flex>

                        <Flex
                            gap="small"
                            align="center"
                            className="shrink-0 whitespace-nowrap xl:ml-4"
                        >
                            {user?.companies && user?.companies?.length > 1 && (
                                <Select
                                    className="min-w-40"
                                    value={activeCompany?.id}
                                    onChange={(companyId) => {
                                        updateActiveCompanyMutation.mutate(companyId)
                                    }}
                                    options={user?.companies?.map((company) => {
                                        return {
                                            label: company.name,
                                            value: company.id,
                                        }
                                    })}
                                />
                            )}
                            <button
                                type="button"
                                onClick={() => setSearchOpen(true)}
                                className="flex items-center gap-2 rounded-full border border-hairline-strong bg-white px-4 py-1.5 text-sm text-ink-tertiary transition-colors hover:border-primary/40 xl:min-w-[240px]"
                            >
                                <SearchOutlined />
                                <span className="mr-auto">{t('Search')}</span>
                                <Typography.Text keyboard>/</Typography.Text>
                            </button>

                            <Search
                                open={searchOpen}
                                onCancel={() => setSearchOpen(false)}
                            />

                            <LocaleSwitch />

                            <Tooltip title={t('Update profile')}>
                                <Link to="/profile">
                                    <Button
                                        type="text"
                                        style={{
                                            color: token.colorText,
                                        }}
                                        className="hover:text-primary"
                                        icon={<UserOutlined />}
                                    />
                                </Link>
                            </Tooltip>

                            <Tooltip title={t('Sign out')}>
                                <Button
                                    type="text"
                                    style={{
                                        color: token.colorText,
                                    }}
                                    className="hover:text-primary"
                                    onClick={() => {
                                        logout()
                                        //   ????????????????
                                        setTimeout(() => {
                                            router.invalidate().finally(() => {
                                                return navigate({ to: '/login' })
                                            })
                                        }, 0)
                                    }}
                                    icon={<LogoutOutlined />}
                                />
                            </Tooltip>
                        </Flex>
                    </Flex>

                    {(isImpersonating || paymentFailing || (user && !user.emailVerifiedAt)) && (
                        <Flex
                            vertical
                            className="mb-6"
                            gap="small"
                        >
                            {isImpersonating && (
                                <Alert
                                    showIcon
                                    message={t('You are currently impersonating {{name}}', { name: user?.name })}
                                    action={
                                        <Button
                                            className="ml-2"
                                            onClick={() => stopImpersonation()}
                                            icon={<LogoutOutlined />}
                                            type="text"
                                        />
                                    }
                                />
                            )}
                            {paymentFailing && <PaymentFailingAlert />}
                            {!isImpersonating && user && !user.emailVerifiedAt && <NotVerifiedAlert user={user} />}
                        </Flex>
                    )}
                    <Flex
                        gap="large"
                        className="flex-col pb-10 xl:flex-row"
                    >
                        <nav className="shadow-card hidden shrink-0 self-start rounded-xl border border-hairline bg-white p-2 xl:block xl:w-[300px]">
                            <Menu
                                selectedKeys={selectedKeys}
                                onClick={({ key }) => navigate({ to: key })}
                                items={menuItems}
                                mode="inline"
                            />
                        </nav>
                        <Card className="shadow-card w-full min-w-0">{children}</Card>
                    </Flex>
                </Container>
            </Content>
        </Layout>
    )
}
